import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Sliders, Save, RotateCcw } from 'lucide-react'
import { ApiClientError, apiFetch } from '@/lib/api-client'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { useAuthStore } from '@/lib/auth-store'

interface OrgTogglesPayload {
  organisationName: string
  plan: string
  /** Org's stored map — explicit overrides only (absent = inherit plan default). */
  stored: Record<string, boolean>
  /** Resolved per-feature state after applying stored map over plan defaults. */
  resolved: Record<string, boolean>
  /** Canonical list of feature keys the UI can toggle. */
  available: readonly string[]
}

const SECTION_GROUPS: Array<{ label: string; keys: string[] }> = [
  {
    label: 'Performance flow',
    keys: [
      'pipeline', 'shows', 'ticketing', 'merch', 'travel',
      'logistics', 'territory', 'broker', 'contracts',
      'setlists', 'debriefs', 'wellness', 'cancellations', 'vip',
    ],
  },
  {
    label: 'Artist-level',
    keys: ['press', 'marketing', 'analytics', 'royalties', 'team'],
  },
  {
    label: 'Tooling',
    keys: ['crew_pool', 'team_permissions', 'email_rules', 'assistant'],
  },
]

const FEATURE_LABELS: Record<string, string> = {
  pipeline: 'Pipeline & inbox',
  shows: 'Shows',
  ticketing: 'Ticketing',
  merch: 'Merch',
  travel: 'Travel',
  logistics: 'Logistics',
  territory: 'Territory routing',
  broker: 'Brokers',
  contracts: 'Contracts',
  setlists: 'Setlists',
  debriefs: 'Debriefs',
  wellness: 'Wellness Guardian',
  cancellations: 'Cancellations',
  vip: 'VIP Experiences',
  press: 'Press & Media',
  marketing: 'Marketing & Advertising',
  analytics: 'Analytics',
  royalties: 'Royalties',
  team: 'Team',
  crew_pool: 'Crew talent pool',
  team_permissions: 'Team permissions',
  email_rules: 'Email rules',
  assistant: 'AI Assistant',
}

export function OrgSettingsPage() {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const { data, error, isLoading } = useQuery<OrgTogglesPayload, ApiClientError>({
    queryKey: ['org-modules'],
    queryFn: () => apiFetch<OrgTogglesPayload>('/organisations/me/modules'),
    enabled: !!user?.organisationId,
  })

  // Local pending patch: feature key -> bool|null (null = clear override / inherit plan)
  const [pending, setPending] = useState<Record<string, boolean | null>>({})
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)

  useEffect(() => {
    setPending({})
  }, [data?.stored])

  const canEdit = user?.orgRole === 'owner' || user?.orgRole === 'admin'

  // Current effective state for each key — what the UI shows in the toggle.
  const effective = useMemo(() => {
    if (!data) return {} as Record<string, boolean>
    const out: Record<string, boolean> = { ...data.resolved }
    for (const [k, v] of Object.entries(pending)) {
      if (v === null) {
        // back to plan default — we don't know plan default per-key locally,
        // but for the UI it's fine: revert to whatever was resolved on load
        // for the org BEFORE any stored override (best effort).
        out[k] = data.resolved[k] ?? false
      } else if (v !== undefined) {
        out[k] = v
      }
    }
    return out
  }, [data, pending])

  function setKey(key: string, value: boolean) {
    setPending((prev) => ({ ...prev, [key]: value }))
  }

  function clearKey(key: string) {
    setPending((prev) => ({ ...prev, [key]: null }))
  }

  async function save() {
    if (Object.keys(pending).length === 0) return
    setSaving(true)
    setSaveErr(null)
    try {
      await apiFetch('/organisations/me/modules', {
        method: 'PATCH',
        body: { patch: pending },
      })
      await qc.invalidateQueries({ queryKey: ['org-modules'] })
      await qc.invalidateQueries({ queryKey: ['nav-artist-modules'] })
      setPending({})
    } catch (err) {
      setSaveErr(err instanceof ApiClientError ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  if (!user?.organisationId) {
    return (
      <div>
        <PageHeader title="Org Settings & Modules" subtitle="You're not in an organisation." />
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Org Settings & Modules"
        subtitle={
          data
            ? `${data.organisationName} • Plan: ${data.plan}`
            : 'Enable or disable modules across every artist in the organisation.'
        }
        actions={
          Object.keys(pending).length > 0 && (
            <button
              type="button"
              onClick={save}
              disabled={saving || !canEdit}
              className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : `Save ${Object.keys(pending).length} change${Object.keys(pending).length === 1 ? '' : 's'}`}
            </button>
          )
        }
      />
      {saveErr && (
        <div className="mb-4 rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">{saveErr}</div>
      )}
      {!canEdit && (
        <div className="mb-4 rounded-md bg-amber-50 px-4 py-2 text-sm text-amber-800">
          You can view module toggles but only org owners and admins can change them.
        </div>
      )}
      <AsyncBoundary isLoading={isLoading} error={error}>
        {data && (
          <div className="space-y-6">
            {SECTION_GROUPS.map((group) => (
              <section key={group.label} className="rounded-lg border border-ovation-ink/10 bg-white p-5">
                <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-ovation-ink">
                  <Sliders className="h-4 w-4" />
                  {group.label}
                </h2>
                <p className="mb-4 text-xs text-ovation-muted">
                  Switch off to hide a module from every artist in this organisation. Per-artist overrides can re-enable individually.
                </p>
                <ul className="divide-y divide-ovation-ink/5">
                  {group.keys
                    .filter((k) => (data.available as readonly string[]).includes(k))
                    .map((key) => {
                      const isPending = pending[key] !== undefined
                      const hasOverride = Object.prototype.hasOwnProperty.call(data.stored, key)
                      const value = effective[key] ?? false
                      return (
                        <li
                          key={key}
                          className="flex flex-wrap items-center justify-between gap-3 py-3"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-ovation-ink">
                              {FEATURE_LABELS[key] ?? key}
                            </div>
                            <div className="text-[11px] uppercase tracking-wider text-ovation-muted">
                              {isPending ? 'Pending' : hasOverride ? 'Overridden' : 'Plan default'}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {(hasOverride || isPending) && (
                              <button
                                type="button"
                                onClick={() => clearKey(key)}
                                disabled={!canEdit}
                                title="Revert to plan default"
                                className="rounded-md border border-ovation-ink/15 bg-white px-2 py-1 text-xs text-ovation-muted hover:bg-ovation-paper disabled:opacity-50"
                              >
                                <RotateCcw className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setKey(key, !value)}
                              disabled={!canEdit}
                              role="switch"
                              aria-checked={value}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${value ? 'bg-ovation-ink' : 'bg-ovation-ink/15'} disabled:opacity-50`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`}
                              />
                            </button>
                          </div>
                        </li>
                      )
                    })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  )
}
